import React, { useEffect, useMemo, useRef, useState } from 'react';
import styles from './DirectoryTree.module.css';
import { FolderClosed, FolderOpen, Document } from './icons';

// Two-column (Miller / NeXT-style) directory navigation.
//
// Layout:
//   - Left column  → the four top-level directories. Selecting one sets the
//     active folder (no inline expansion).
//   - Right column → the children of the active folder. Selecting a child
//     fires `onSelect(leafKey)` so the [FIG. 2] frame swaps.
//
// Accessibility: each column is its own `tree` with a roving tabindex (exactly
// one focusable row per column). Folders are treeitems with aria-expanded
// reflecting whether they are the active column; the active folder is also
// aria-selected. Leaves are treeitems with aria-selected reflecting the chosen
// leaf.
//
// Keyboard contract:
//   ArrowDown / ArrowUp  → move focus within the current column
//   ArrowRight           → from the folder column, move into the children column
//   ArrowLeft            → from the children column, move back to the folders
//   Home / End           → first / last row in the current column
//   Enter / Space        → activate (folder → set active; leaf → select)
//
// `activeFolderKey` is owned here; `selectedKey` (the chosen leaf) is lifted to
// the parent and passed back down so the selection stays highlighted.

function DirectoryTree({ data, selectedKey, onSelect }) {
  const [activeFolderKey, setActiveFolderKey] = useState(data[0]?.key ?? null);
  // Which column currently holds focus, and the focused row key within it.
  const [focus, setFocus] = useState({ column: 'folders', key: data[0]?.key ?? null });

  const folderRefs = useRef(new Map());
  const childRefs = useRef(new Map());

  const activeFolder = useMemo(
    () => data.find((folder) => folder.key === activeFolderKey) ?? null,
    [data, activeFolderKey]
  );
  const children = useMemo(
    () => activeFolder?.children ?? [],
    [activeFolder]
  );

  // Keep the children-column focus valid when the active folder changes.
  useEffect(() => {
    if (focus.column !== 'children') return;
    if (!children.some((leaf) => leaf.key === focus.key)) {
      setFocus({ column: 'children', key: children[0]?.key ?? null });
    }
  }, [children, focus]);

  function refsFor(column) {
    return column === 'folders' ? folderRefs : childRefs;
  }

  function moveFocus(column, key) {
    setFocus({ column, key });
    const el = refsFor(column).current.get(key);
    if (el) el.focus();
  }

  function activateFolder(key) {
    setActiveFolderKey(key);
    setFocus({ column: 'folders', key });
  }

  function activateLeaf(key) {
    onSelect(key);
    setFocus({ column: 'children', key });
  }

  function handleKeyDown(event, column) {
    const list = column === 'folders' ? data : children;
    const index = list.findIndex((item) => item.key === focus.key);

    switch (event.key) {
      case 'ArrowDown': {
        event.preventDefault();
        const next = list[Math.min(index + 1, list.length - 1)];
        if (next) moveFocus(column, next.key);
        break;
      }
      case 'ArrowUp': {
        event.preventDefault();
        const prev = list[Math.max(index - 1, 0)];
        if (prev) moveFocus(column, prev.key);
        break;
      }
      case 'ArrowRight': {
        event.preventDefault();
        if (column === 'folders' && children.length > 0) {
          // Make sure the folder under focus is the active one before crossing.
          if (focus.key !== activeFolderKey) setActiveFolderKey(focus.key);
          const target =
            children.find((leaf) => leaf.key === selectedKey) ?? children[0];
          moveFocus('children', target.key);
        }
        break;
      }
      case 'ArrowLeft': {
        event.preventDefault();
        if (column === 'children') {
          moveFocus('folders', activeFolderKey);
        }
        break;
      }
      case 'Home': {
        event.preventDefault();
        if (list[0]) moveFocus(column, list[0].key);
        break;
      }
      case 'End': {
        event.preventDefault();
        const last = list[list.length - 1];
        if (last) moveFocus(column, last.key);
        break;
      }
      case 'Enter':
      case ' ': {
        event.preventDefault();
        if (column === 'folders') activateFolder(focus.key);
        else if (focus.key) activateLeaf(focus.key);
        break;
      }
      default:
        break;
    }
  }

  const folderFocusKey =
    focus.column === 'folders' ? focus.key : activeFolderKey;
  const childFocusKey =
    focus.column === 'children' ? focus.key : children[0]?.key ?? null;

  return (
    <div className={styles.columns}>
      <ul
        className={styles.tree}
        role="tree"
        aria-label="Directories"
        onKeyDown={(e) => handleKeyDown(e, 'folders')}
      >
        {data.map((folder) => {
          const isActive = folder.key === activeFolderKey;
          const isFocusRow = folder.key === folderFocusKey;
          return (
            <li
              key={folder.key}
              role="treeitem"
              aria-expanded={isActive}
              aria-selected={isActive}
            >
              <div
                ref={(el) => {
                  if (el) folderRefs.current.set(folder.key, el);
                  else folderRefs.current.delete(folder.key);
                }}
                className={`${styles.row} ${isActive ? styles.active : ''}`}
                tabIndex={isFocusRow ? 0 : -1}
                onClick={() => activateFolder(folder.key)}
              >
                <span className={styles.icon}>
                  {isActive ? <FolderOpen /> : <FolderClosed />}
                </span>
                <span className={styles.label}>{folder.label}</span>
                <span className={styles.count}>({folder.children.length})</span>
              </div>
            </li>
          );
        })}
      </ul>

      <ul
        className={`${styles.tree} ${styles.childTree}`}
        role="tree"
        aria-label={
          activeFolder ? `${activeFolder.label} contents` : 'Contents'
        }
        onKeyDown={(e) => handleKeyDown(e, 'children')}
      >
        {children.map((leaf) => {
          const leafSelected = selectedKey === leaf.key;
          const isFocusRow = leaf.key === childFocusKey;
          return (
            <li key={leaf.key} role="treeitem" aria-selected={leafSelected}>
              <div
                ref={(el) => {
                  if (el) childRefs.current.set(leaf.key, el);
                  else childRefs.current.delete(leaf.key);
                }}
                className={`${styles.row} ${styles.leafRow} ${
                  leafSelected ? styles.selected : ''
                }`}
                tabIndex={isFocusRow ? 0 : -1}
                onClick={() => activateLeaf(leaf.key)}
              >
                <span className={styles.icon}>
                  <Document />
                </span>
                <span className={styles.label}>{leaf.label}</span>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export default DirectoryTree;
